import { driver } from 'driver.js';
import 'driver.js/dist/driver.css';
import { useI18n } from 'vue-i18n';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { TOUR_DEFINITIONS, TOURS_BY_ROUTE } from 'dashboard/helper/tours';

// Tour guiado de treinamento (driver.js): destaca os elementos reais da tela
// com balões passo a passo. Passos com `click: true` são INTERATIVOS — o
// "Próximo" some e o cliente só avança clicando de verdade no elemento
// destacado (se o clique navegar, o tour da tela nova assume). Conclusão em
// ui_settings.tours_completed (por usuário).

// Instância única: iniciar um tour derruba o anterior; troca de rota idem.
let activeTour = null;
// Limpeza do listener do passo interativo corrente — sem isso, cada
// re-execução EMPILHAVA um listener no mesmo elemento e um clique disparava
// vários "próximo" (o tour ficava quicando entre passos eternamente).
let clickCleanup = null;

export const destroyActiveTour = () => {
  clickCleanup?.();
  clickCleanup = null;
  if (!activeTour) return;
  const tour = activeTour;
  activeTour = null;
  tour.destroy();
};

export function useTour() {
  const { t } = useI18n();
  const { uiSettings, updateUISettings } = useUISettings();

  const completedTours = () => uiSettings.value?.tours_completed || {};
  const isTourCompleted = key => Boolean(completedTours()[key]);

  // Opt-out global: usuário desativa os tours automáticos (refazer manual
  // pelo botão "?" continua funcionando).
  const toursDisabled = () => Boolean(uiSettings.value?.tours_disabled);
  const setToursDisabled = value =>
    updateUISettings({ tours_disabled: Boolean(value) });

  // Zera o histórico: a experiência de onboarding roda inteira de novo, como
  // para um usuário novo (útil pra demonstrar/treinar).
  const resetTours = () =>
    updateUISettings({ tours_completed: {}, tours_disabled: false });

  const markTourCompleted = key => {
    updateUISettings({
      tours_completed: { ...completedTours(), [key]: true },
    });
  };

  // Clique real no elemento destacado → avança (com um respiro pra modais/
  // dropdowns abrirem antes do próximo destaque). Um listener por vez, e o
  // avanço só vale para a INSTÂNCIA de tour que o criou (timers velhos não
  // mexem num tour novo).
  const attachClickAdvance = (element, step) => {
    if (!element) return;
    clickCleanup?.();
    const tourRef = activeTour;
    const handler = () => {
      clickCleanup?.();
      clickCleanup = null;
      setTimeout(() => {
        if (!activeTour || activeTour !== tourRef) return;
        if (activeTour.isLastStep()) destroyActiveTour();
        else activeTour.moveNext();
      }, step.delay || 400);
    };
    element.addEventListener('click', handler);
    clickCleanup = () => element.removeEventListener('click', handler);
  };

  const buildSteps = key => {
    const definition = TOUR_DEFINITIONS[key];
    if (!definition) return [];
    return definition.steps
      .filter(
        step =>
          !step.element || step.lazy || document.querySelector(step.element)
      )
      .map(step => {
        const description = step.click
          ? `${t(`TOUR.${key.toUpperCase()}.${step.key}.TEXT`)}<br /><em>👆 ${t('TOUR.CONTROLS.CLICK_HINT')}</em>`
          : t(`TOUR.${key.toUpperCase()}.${step.key}.TEXT`);
        return {
          ...(step.element ? { element: step.element } : {}),
          popover: {
            title: t(`TOUR.${key.toUpperCase()}.${step.key}.TITLE`),
            description,
            ...(step.click ? { showButtons: ['previous', 'close'] } : {}),
          },
          ...(step.click
            ? { onHighlighted: element => attachClickAdvance(element, step) }
            : {}),
        };
      });
  };

  const startTour = key => {
    destroyActiveTour();
    const steps = buildSteps(key);
    if (!steps.length) return;

    activeTour = driver({
      showProgress: true,
      allowClose: true,
      overlayOpacity: 0.6,
      stagePadding: 6,
      stageRadius: 10,
      nextBtnText: t('TOUR.CONTROLS.NEXT'),
      prevBtnText: t('TOUR.CONTROLS.PREV'),
      doneBtnText: t('TOUR.CONTROLS.DONE'),
      // {{current}}/{{total}} são placeholders do driver.js — fora do i18n
      // porque o vue-i18n interpretaria as chaves duplas como interpolação.
      progressText: `{{current}} ${t('TOUR.CONTROLS.OF')} {{total}}`,
      steps,
      onDestroyed: () => {
        activeTour = null;
        markTourCompleted(key);
      },
    });
    activeTour.drive();
  };

  const tourForRoute = routeName => TOURS_BY_ROUTE[routeName] || null;

  // Primeira visita do usuário: visão geral primeiro (uma vez), depois o tour
  // da tela atual (uma vez cada). Chamado pelo layout a cada troca de rota.
  const maybeAutoStart = routeName => {
    if (!uiSettings.value) return;
    if (toursDisabled()) return;
    if (!isTourCompleted('overview')) {
      startTour('overview');
      return;
    }
    const key = tourForRoute(routeName);
    if (key && !isTourCompleted(key)) startTour(key);
  };

  return {
    startTour,
    maybeAutoStart,
    tourForRoute,
    isTourCompleted,
    toursDisabled,
    setToursDisabled,
    resetTours,
  };
}
